// MyTasks.jsx — Personal task widget shown on HomePage.
// Aggregates tasks across modules (meetings, errors, events, campaigns) where current user is the owner/assignee.
import React, { useEffect, useState, useCallback } from "react";
import { api } from "./lib/api";
import { formatKSADate } from "./lib/ksaTime";

const SOURCE_STYLE = {
  meeting:  { bg: "rgba(59,130,246,0.10)", color: "#1E6390", label: "اجتماع" },
  error:    { bg: "rgba(239,68,68,0.10)",  color: "#B71C1C", label: "خطأ/درس" },
  event:    { bg: "rgba(20,184,166,0.10)", color: "#0F766E", label: "فعالية" },
  campaign: { bg: "rgba(236,72,153,0.10)", color: "#9D174D", label: "حملة" },
  mystery:  { bg: "rgba(139,92,246,0.12)", color: "#6D28D9", label: "عميل سرّي" },
};

const DUE_STYLE = {
  overdue:  { color: "var(--danger)",  icon: "🔥", label: "متأخرة" },
  today:    { color: "var(--warn)",    icon: "⏰", label: "اليوم" },
  upcoming: { color: "var(--info)",    icon: "📅", label: "قريبة" },
  none:     { color: "var(--muted)",   icon: "—",  label: "بدون موعد" },
};

const STATUS_LABEL = {
  open: "🆕",
  in_progress: "⏳",
  done: "✅",
  blocked: "🚧",
  postponed: "⏸",
  investigating: "🔎",
  monitoring: "👁",
  planned: "📅",
  active: "🟢",
};

const STATUS_OPTIONS_TASK = [
  { id: "in_progress", label: "⏳ جارية" },
  { id: "done",        label: "✅ منفذة" },
  { id: "postponed",   label: "⏸ مؤجلة" },
];
// Errors use slightly different terminology
const STATUS_OPTIONS_ERR = [
  { id: "in_progress", label: "⏳ جارية" },
  { id: "resolved",    label: "✅ تم الحل" },
  { id: "postponed",   label: "⏸ مؤجلة" },
];

function statusOptionsFor(source) {
  return source === "error" ? STATUS_OPTIONS_ERR : STATUS_OPTIONS_TASK;
}

function endpointFor(t) {
  if (t.source === "meeting") return `/tasks/meeting-task/${t.parent_id}/${t.task_id}/status`;
  if (t.source === "event")   return `/tasks/event-req/${t.parent_id}/${t.task_id}/status`;
  if (t.source === "error")   return `/tasks/error/${t.task_id}/status`;
  if (t.source === "mystery") return `/mystery/tasks/${t.task_id}/status`;
  if (t.source === "executive") return `/tasks/executive/${t.task_id}/status`;
  return null; // campaigns: no inline editing (admin/marketing manages them)
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    return formatKSADate(new Date(s), { day: "numeric", month: "short" });
  } catch { return s; }
}

export default function MyTasks({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [postponeReasonFor, setPostponeReasonFor] = useState(null); // task key
  const [reasonText, setReasonText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/tasks/my");
      setData(d);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (t, newStatus, reason) => {
    const url = endpointFor(t);
    if (!url) return;
    const taskKey = `${t.source}-${t.task_id}`;
    setSavingId(taskKey);
    try {
      await api.patch(url, {
        status: newStatus,
        source: t.source,
        parent_id: t.parent_id,
        reason: reason || null,
      });
      setEditingId(null);
      setPostponeReasonFor(null);
      setReasonText("");
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "تعذّر تحديث الحالة";
      alert(msg);
    } finally { setSavingId(null); }
  };

  if (loading) return null;
  if (!data || data.counts.total === 0) {
    return (
      <div className="card" data-testid="my-tasks-empty" style={{
        marginBottom: 16,
        padding: 14,
        background: "linear-gradient(135deg, rgba(16,185,129,0.05) 0%, rgba(20,184,166,0.05) 100%)",
        borderInlineStart: "3px solid var(--success)",
      }}>
        <h4 style={{ margin: 0, color: "var(--brown-deep)" }}>📋 مهامي</h4>
        <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "var(--muted)", lineHeight: 1.8 }}>
          ✨ لا توجد مهام مُسنَدة إليك حالياً — استمتع بقهوتك ☕
        </p>
      </div>
    );
  }

  const items = expanded ? data.items : data.items.slice(0, 5);
  const c = data.counts;

  return (
    <div className="card" data-testid="my-tasks" style={{ marginBottom: 16, padding: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h4 style={{ margin: 0, color: "var(--brown-deep)" }}>📋 مهامي ({c.total})</h4>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
            {c.overdue > 0 && <span style={{ color: "var(--danger)", marginInlineEnd: 8 }}>🔥 {c.overdue} متأخرة</span>}
            {c.today > 0 && <span style={{ color: "var(--warn)", marginInlineEnd: 8 }}>⏰ {c.today} اليوم</span>}
            {c.upcoming > 0 && <span style={{ color: "var(--info)" }}>📅 {c.upcoming} قريبة</span>}
          </div>
        </div>
        <button type="button" onClick={load} data-testid="refresh-my-tasks"
                className="btn btn-soft" style={{ fontSize: 11, padding: "4px 10px" }}>
          🔄 تحديث
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="my-tasks-list">
        {items.map((t) => {
          const ss = SOURCE_STYLE[t.source] || SOURCE_STYLE.meeting;
          const ds = DUE_STYLE[t.due_class] || DUE_STYLE.none;
          const taskKey = `${t.source}-${t.task_id}`;
          const canEdit = !!endpointFor(t);
          const isEditing = editingId === taskKey;
          const isSaving = savingId === taskKey;
          const showReasonInput = postponeReasonFor === taskKey;
          return (
            <div
              key={taskKey}
              data-testid={`task-${t.source}-${t.task_id}`}
              style={{
                padding: "10px 12px",
                background: "var(--surface, #fff)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                borderInlineStart: `3px solid ${ds.color}`,
                transition: "transform 120ms ease, box-shadow 120ms ease",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", cursor: "pointer" }}
                onClick={() => onNavigate?.(t.nav_tab)}
              >
                <span style={{ fontSize: 16 }}>{t.icon}</span>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--brown-deep)", lineHeight: 1.4 }}>
                    {STATUS_LABEL[t.status] || "•"} {t.title}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                    <span style={{ background: ss.bg, color: ss.color, padding: "1px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
                      {ss.label}
                    </span>
                    {" · "}{t.parent_title || "—"}
                    {t.status === "postponed" && t.postpone_reason && (
                      <span style={{ marginInlineStart: 8, color: "var(--warn)", fontWeight: 600 }}>
                        ⏸ مؤجلة: {t.postpone_reason}
                      </span>
                    )}
                  </div>
                  {/* v119.131 — Show Claude-enriched task description + audience */}
                  {t.description && (
                    <div style={{ fontSize: 11.5, color: "#3E2723", marginTop: 6,
                                     padding: "5px 8px", background: "#fef9e7",
                                     borderRadius: 4, lineHeight: 1.5 }}>
                      📝 {t.description}
                    </div>
                  )}
                  {(t.target_audience || t.impact_metric) && (
                    <div style={{ fontSize: 10.5, color: "#7B5804", marginTop: 4,
                                     display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {t.target_audience && <span>🎯 {t.target_audience}</span>}
                      {t.impact_metric && (
                        <span>📊 {t.impact_metric}{t.impact_target ? ` = ${Number(t.impact_target).toLocaleString()}` : ""}</span>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "end", minWidth: 80 }}>
                  <div style={{ fontSize: 11, color: ds.color, fontWeight: 700 }}>
                    {ds.icon} {ds.label}
                  </div>
                  {t.due_date && (
                    <div style={{ fontSize: 10, color: "var(--muted)" }}>{fmtDate(t.due_date)}</div>
                  )}
                </div>
                {canEdit && (
                  <button
                    type="button"
                    data-testid={`task-edit-${taskKey}`}
                    onClick={(e) => { e.stopPropagation(); setEditingId(isEditing ? null : taskKey); setPostponeReasonFor(null); }}
                    style={{
                      padding: "4px 10px", borderRadius: 6, cursor: "pointer",
                      background: isEditing ? "var(--brown)" : "var(--beige)",
                      color: isEditing ? "#fff" : "var(--brown-deep)",
                      border: "1px solid var(--border)", fontSize: 11, fontWeight: 700,
                    }}
                  >{isEditing ? "✕" : "✏ تعديل"}</button>
                )}
              </div>

              {/* Inline status editor */}
              {isEditing && canEdit && (
                <div data-testid={`task-editor-${taskKey}`} style={{
                  padding: "8px 10px", background: "var(--beige)", borderRadius: 6,
                  display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center",
                }}>
                  <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700 }}>غيّر الحالة إلى:</span>
                  {statusOptionsFor(t.source).map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      data-testid={`task-status-${taskKey}-${opt.id}`}
                      disabled={isSaving}
                      onClick={() => {
                        if (opt.id === "postponed") {
                          setPostponeReasonFor(taskKey);
                        } else {
                          updateStatus(t, opt.id, null);
                        }
                      }}
                      style={{
                        padding: "5px 12px", borderRadius: 6, cursor: isSaving ? "wait" : "pointer",
                        background: t.status === opt.id ? "var(--brown)" : "#fff",
                        color: t.status === opt.id ? "#fff" : "var(--brown-deep)",
                        border: "1px solid var(--border)", fontSize: 11.5, fontWeight: 700,
                      }}
                    >{opt.label}</button>
                  ))}
                </div>
              )}

              {/* Postpone reason input */}
              {showReasonInput && (
                <div data-testid={`task-reason-${taskKey}`} style={{
                  padding: "10px 12px", background: "rgba(245,158,11,0.08)",
                  borderRadius: 6, border: "1px solid rgba(245,158,11,0.3)",
                }}>
                  <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 6 }}>
                    ✍ سبب التأجيل (مطلوب):
                  </label>
                  <textarea
                    value={reasonText}
                    onChange={(e) => setReasonText(e.target.value)}
                    placeholder="مثال: انتظار توريد المواد · مكلَّف بأولوية أعلى ..."
                    rows={2}
                    data-testid={`task-reason-input-${taskKey}`}
                    style={{
                      width: "100%", padding: "8px 10px", border: "1px solid var(--border)",
                      borderRadius: 6, fontSize: 12, fontFamily: "var(--f-n)", resize: "vertical",
                    }}
                  />
                  <div style={{ display: "flex", gap: 6, marginTop: 6, justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      onClick={() => { setPostponeReasonFor(null); setReasonText(""); }}
                      style={{ padding: "5px 12px", borderRadius: 6, cursor: "pointer", background: "transparent", color: "var(--muted)", border: "1px solid var(--border)", fontSize: 11.5, fontWeight: 600 }}
                    >إلغاء</button>
                    <button
                      type="button"
                      data-testid={`task-reason-save-${taskKey}`}
                      disabled={isSaving || !reasonText.trim()}
                      onClick={() => updateStatus(t, "postponed", reasonText)}
                      style={{
                        padding: "5px 14px", borderRadius: 6, cursor: reasonText.trim() ? "pointer" : "not-allowed",
                        background: reasonText.trim() ? "#D97706" : "var(--muted)",
                        color: "#fff", border: "none", fontSize: 11.5, fontWeight: 700,
                      }}
                    >{isSaving ? "⏳ حفظ..." : "💾 تأجيل"}</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {data.items.length > 5 && (
        <button type="button"
                onClick={() => setExpanded(!expanded)}
                data-testid="toggle-my-tasks-expand"
                className="btn btn-soft"
                style={{ marginTop: 10, width: "100%", fontSize: 12 }}>
          {expanded ? `🔼 إخفاء (إظهار 5)` : `🔽 إظهار الباقي (${data.items.length - 5})`}
        </button>
      )}
    </div>
  );
}
